// src/components/IncomingOrders/ViewDialog/FilterSettingsPanel.jsx

import PropTypes from "prop-types";

import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
} from "@mui/material";

import ORDER_STATUSES from "../helpers/orderCategories";
import ORDER_ROW_TYPE_META from "../helpers/types";
import SettingsPopupMenu from "./SettingsPopupMenu";

const sectionHeaderSx = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  px: 2,
  py: 0.75,
  bgcolor: "background.default",
  borderBottom: "1px solid",
  borderColor: "divider",
  fontSize: 10,
  fontWeight: 700,
  lineHeight: "normal",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "text.secondary",
};

const FilterSettingsPanel = ({
  anchorEl,
  filters,
  open,
  statuses,
  onClearStatuses,
  onClose,
  onOrderStatusChange,
  onToggleStatus,
  onToggleType,
}) => {
  const intl = useIntl();

  const orderStatusLabel = intl.formatMessage({
    id: "incomingOrders.views.orderStatus",
  });

  return (
    <SettingsPopupMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      title={
        <FormattedMessage id="incomingOrders.views.filtering" />
      }
      subtitle={
        <FormattedMessage id="incomingOrders.views.selectFiltersHelp" />
      }
    >
      <ListSubheader disableSticky={false} sx={sectionHeaderSx}>
        <FormattedMessage id="incomingOrders.views.productType" />
      </ListSubheader>

      {Object.entries(ORDER_ROW_TYPE_META).map(([key, type]) => (
        <MenuItem
          key={key}
          dense
          onClick={() => onToggleType(key)}
          sx={{
            px: 1,
            py: 0,
          }}
        >
          <Checkbox
            size="small"
            checked={filters.productTypes.includes(key)}
          />
          <ListItemText primary={`${type.icon} ${type.label}`} />
        </MenuItem>
      ))}

      <ListSubheader disableSticky={false} sx={sectionHeaderSx}>
        <FormattedMessage id="incomingOrders.views.orderStatus" />
      </ListSubheader>

      <Box sx={{ px: 2, py: 1 }}>
        <FormControl fullWidth size="small">
          <InputLabel>{orderStatusLabel}</InputLabel>

          <Select
            value={filters.orderStatus ?? ""}
            label={orderStatusLabel}
            onChange={(event) =>
              onOrderStatusChange(event.target.value || null)
            }
            MenuProps={{
              disablePortal: true,
            }}
          >
            <MenuItem value="">
              <FormattedMessage id="incomingOrders.views.all" />
            </MenuItem>

            {ORDER_STATUSES.map((status) => (
              <MenuItem key={status.key} value={status.key}>
                {status.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <ListSubheader disableSticky={false} sx={sectionHeaderSx}>
        <FormattedMessage id="incomingOrders.views.supplierOrderStatus" />
      </ListSubheader>

      <MenuItem
        dense
        disabled={filters.supplierOrderStatusIds.length === 0}
        onClick={onClearStatuses}
        sx={{
          px: 1,
          py: 0,
        }}
      >
        <Checkbox
          size="small"
          checked={filters.supplierOrderStatusIds.length === 0}
          indeterminate={filters.supplierOrderStatusIds.length > 0}
        />
        <ListItemText
          primary={
            <FormattedMessage id="incomingOrders.toolbar.allStatuses" />
          }
        />
      </MenuItem>

      {statuses?.map((status) => {
        const checked =
          filters.supplierOrderStatusIds.includes(status.id);

        return (
          <MenuItem
            key={status.id}
            dense
            onClick={() => onToggleStatus(status.id)}
            sx={{
              px: 1,
              py: 0,
            }}
          >
            <Checkbox size="small" checked={checked} />
            <ListItemText primary={status.name} />
          </MenuItem>
        );
      })}
    </SettingsPopupMenu>
  );
};

FilterSettingsPanel.propTypes = {
  anchorEl: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      nodeType: PropTypes.number,
    }),
  ]),
  filters: PropTypes.shape({
    productTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
    orderStatus: PropTypes.string,
    supplierOrderStatusIds: PropTypes.arrayOf(
      PropTypes.number
    ).isRequired,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  statuses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  onClearStatuses: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onOrderStatusChange: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
  onToggleType: PropTypes.func.isRequired,
};

export default FilterSettingsPanel;
